import { useOutletContext } from "react-router-dom"
import { ActivityCategoryInfo, ActivityOutletContext } from "../../interface"
import { Button, Card, Checkbox, Col, message, Row, Select, Typography } from "antd"
import { useState, useEffect, useMemo } from "react"
import { downloadActivityBracketPdf, downloadActivitySchedulePdf } from "../../apiBridge"
import {
    canIncludeBracketClassification,
    categoryHasDownloadableMatches,
    downloadUnavailableCopy,
    getUniqueScheduledCourts,
} from "./downloadUtils"
import "./download.css"

const { Option } = Select;
const { Title } = Typography;
type ScheduleOrientation = 'portrait' | 'landscape';

const ActivityDownload: React.FC = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const { activity, categories } = useOutletContext() as ActivityOutletContext

    const [nowDate, setNowDate] = useState<string>(activity.options.start_time[0]?.date || '')
    const [selectedCourts, setSelectedCourts] = useState<number[]>([])
    const [selectedCategory, setSelectedCategory] = useState<string>('')
    const [selectedRounds, setSelectedRounds] = useState<Record<string, boolean>>({})
    const [includeClassification, setIncludeClassification] = useState(false)
    const scheduleOrientation: ScheduleOrientation = 'portrait'

    const availableCategories = useMemo<ActivityCategoryInfo[]>(() => categories || [], [categories]);

    const availableRounds = useMemo(() => {
        if (!selectedCategory) return [];
        const category = categories?.find(cat => String(cat.id) === selectedCategory);
        if (!category) return [];

        return Object.entries(category.options.rounds || {})
            .filter(([_, round]) => {
                return round.schedule?.some(sched => sched.date === nowDate);
            })
            .map(([roundId, round]) => ({
                id: roundId,
                name: round.name,
                courts: getUniqueScheduledCourts(
                    round.schedule?.filter(sched => sched.date === nowDate) || []
                )
            }));
    }, [categories, nowDate, selectedCategory]);

    useEffect(() => {
        setSelectedRounds(previous => {
            const next: Record<string, boolean> = {};
            availableRounds.forEach(round => {
                next[round.id] = previous[round.id] ?? true;
            });

            const previousKeys = Object.keys(previous);
            const nextKeys = Object.keys(next);
            const unchanged = previousKeys.length === nextKeys.length &&
                nextKeys.every(key => previous[key] === next[key]);
            return unchanged ? previous : next;
        });
    }, [availableRounds]);

    useEffect(() => {
        if (availableCategories.length === 0) {
            setSelectedCategory('');
        } else if (!availableCategories.some(cat => String(cat.id) === selectedCategory)) {
            setSelectedCategory(availableCategories[0]?.id ? String(availableCategories[0].id) : '');
        }
    }, [availableCategories, selectedCategory]);

    const selectedDownloadCategory = useMemo(
        () => availableCategories.find(category => String(category.id) === selectedCategory) || null,
        [availableCategories, selectedCategory],
    );
    const classificationAvailable = canIncludeBracketClassification(selectedDownloadCategory);
    const downloadsReady = categoryHasDownloadableMatches(selectedDownloadCategory);
    const unavailableCopy = downloadUnavailableCopy(selectedDownloadCategory);

    useEffect(() => {
        setIncludeClassification(classificationAvailable);
    }, [classificationAvailable]);

    const selectedRoundCount = Object.values(selectedRounds).filter(Boolean).length;
    const canDownloadSchedule = downloadsReady && availableRounds.length > 0 && selectedRoundCount > 0 && selectedCourts.length > 0;
    const canDownloadBracket = downloadsReady;

    const responsePdfBlob = async (response: Response): Promise<Blob> => {
        if (!response.ok) {
            let errorMessage = `Request failed (${response.status})`;
            try {
                const payload = await response.json();
                if (typeof payload?.error === 'string' && payload.error.trim()) {
                    errorMessage = payload.error;
                }
            } catch {
                // Keep the status-based message when the body is not JSON.
            }
            throw new Error(errorMessage);
        }

        const contentType = response.headers.get('Content-Type') || '';
        if (!contentType.toLowerCase().includes('application/pdf')) {
            throw new Error('Server returned an invalid PDF response');
        }
        return response.blob();
    };

    const savePdf = (data: Blob, filename: string) => {
        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const handleCourtChange = (courtNum: number, checked: boolean) => {
        if (checked) {
            setSelectedCourts([...selectedCourts, courtNum]);
        } else {
            setSelectedCourts(selectedCourts.filter(c => c !== courtNum));
        }
    };

    const downloadBracketPDF = async() => {
        if (!selectedCategory || !downloadsReady) {
            messageApi.warning('This category is not configured yet.');
            return;
        }

        try {
            const params = {
                "category_id": Number(selectedCategory),
                "include_classification": classificationAvailable && includeClassification,
            }

            const res = await downloadActivityBracketPdf(activity.id, params);
            const data = await responsePdfBlob(res);
            savePdf(data, `${activity.name}_bracket.pdf`);
        }  catch (error) {
            let errorMessage = "An unknown error occurred";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            messageApi.error(`PDF Download Failed: ${errorMessage}`);
        }
    }

    const downloadTimetablePDF = async () => {
        if (!selectedCategory || !downloadsReady) {
            messageApi.warning('This category is not configured yet.');
            return;
        }

        if (selectedCourts.length === 0) {
            messageApi.warning('Please select at least one court');
            return;
        }

        const selectedRoundIds = Object.entries(selectedRounds)
            .filter(([_, isSelected]) => isSelected)
            .map(([roundId]) => parseInt(roundId));

        if (selectedRoundIds.length === 0) {
            messageApi.warning('Please select at least one round');
            return;
        }

        try {
            const params = {
                "plan_date": nowDate,
                "courts": selectedCourts,
                "category_id": Number(selectedCategory),
                "rounds": selectedRoundIds,
                "orientation": scheduleOrientation
            }

            const res = await downloadActivitySchedulePdf(activity.id, params);

            const data = await responsePdfBlob(res);
            savePdf(data, `${activity.name}_schedule.pdf`);
        } catch (error) {
            let errorMessage = "An unknown error occurred";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            messageApi.error(`PDF Download Failed: ${errorMessage}`);
        }
    }

    return <>
        {contextHolder}
        <Card>
            <Title level={3}>Download</Title>

            <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>Category</div>
                <Select
                    style={{ width: '100%' }}
                    placeholder={availableCategories.length === 0 ? 'No categories available' : 'Select a category'}
                    value={selectedCategory || undefined}
                    onChange={setSelectedCategory}
                    disabled={availableCategories.length === 0}
                >
                    {availableCategories.map((category) => (
                        <Option key={category.id} value={String(category.id)}>
                            {category.name}
                        </Option>
                    ))}
                </Select>
            </div>

            {unavailableCopy ? (
                <div className="activity-download-empty" role="status">
                    <div>{unavailableCopy.title}</div>
                    {unavailableCopy.detail ? (
                        <div className="activity-download-hint">{unavailableCopy.detail}</div>
                    ) : null}
                </div>
            ) : (
            <div className="activity-download-panels">
                <section className="activity-download-panel" aria-labelledby="download-schedule-heading">
                    <div className="activity-download-panel-body">
                        <Title id="download-schedule-heading" level={4}>Schedule PDF</Title>

                        <div style={{ marginBottom: 8 }}>Date</div>
                        <Select
                            style={{ width: '100%', marginBottom: 16 }}
                            value={nowDate || undefined}
                            onChange={(value) => setNowDate(value)}
                            options={
                                activity.options.start_time.map((start_time) => ({
                                    value: start_time.date,
                                    label: start_time.date
                                }))
                            }
                        />

                        <div style={{ marginBottom: 8 }}>Courts</div>
                        <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
                            {Array.from({ length: Math.min(activity.max_court_count || 5, 5) }, (_, i) => i + 1).map((courtNum) => (
                                <Col key={courtNum}>
                                    <Checkbox
                                        checked={selectedCourts.includes(courtNum)}
                                        onChange={(e) => handleCourtChange(courtNum, e.target.checked)}
                                    >
                                        Court {courtNum}
                                    </Checkbox>
                                </Col>
                            ))}
                        </Row>

                        <div style={{ marginBottom: 8 }}>Rounds</div>
                        {selectedCategory && availableRounds.length > 0 ? (
                            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d9d9d9', padding: '8px', borderRadius: '4px', background: '#fff' }}>
                                {availableRounds.map(round => (
                                    <div key={round.id} style={{ marginBottom: 8 }}>
                                        <Checkbox
                                            checked={!!selectedRounds[round.id]}
                                            onChange={(e) => setSelectedRounds(prev => ({
                                                ...prev,
                                                [round.id]: e.target.checked
                                            }))}
                                        >
                                            <strong>{round.name}</strong>
                                        </Checkbox>
                                        <div style={{ marginLeft: 24 }}>
                                            {round.courts.map(court => (
                                                <span key={court} style={{ color: '#666', fontSize: '0.9em', marginRight: 10 }}>
                                                    Court {court}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="activity-download-empty">
                                {selectedCategory
                                    ? 'No matches scheduled for this date.'
                                    : 'Select a category to choose schedule options.'}
                            </div>
                        )}
                    </div>
                    <div className="activity-download-panel-actions">
                        <Button
                            type="primary"
                            onClick={downloadTimetablePDF}
                            disabled={!canDownloadSchedule}
                        >
                            Download Schedule
                        </Button>
                    </div>
                </section>

                <section className="activity-download-panel" aria-labelledby="download-bracket-heading">
                    <div className="activity-download-panel-body">
                        <Title id="download-bracket-heading" level={4}>Bracket PDF</Title>
                        <Checkbox
                            checked={classificationAvailable && includeClassification}
                            disabled={!classificationAvailable}
                            onChange={(event) => setIncludeClassification(event.target.checked)}
                        >
                            Include Classification
                        </Checkbox>
                        <div className="activity-download-hint">
                            {classificationAvailable
                                ? 'Printed only when the bronze match is finished.'
                                : 'This category has no bronze match.'}
                        </div>
                    </div>
                    <div className="activity-download-panel-actions">
                        <Button
                            type="primary"
                            onClick={downloadBracketPDF}
                            disabled={!canDownloadBracket}
                        >
                            Download Bracket
                        </Button>
                    </div>
                </section>
            </div>
            )}
        </Card>
    </>
}

export default ActivityDownload
